import { i18n } from "@dehub/theme";
import { Box, Button, Card, Divider, Grid, Group, Image, Loader, NumberInput, Overlay, Portal, Select, Stack, Text, Textarea, TextInput, Title, rem } from "@mantine/core";
import { useForm } from "@mantine/form";
import { CreditCardIcon, CurrencyCircleDollarIcon, LockSimpleIcon } from "@phosphor-icons/react";
import { yupResolver } from "mantine-form-yup-resolver";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import * as yup from "yup";
import { InvoiceApi } from "../../apis";
import { IAccounts } from "../../interfaces/IAccounts";
import { IGeneral } from "../../interfaces/IGeneral";
import { IReference } from "../../interfaces/IReference";
import { currencyFormat } from "../../utils";
import { message } from "../../utils/Message";
import { dueDay } from "../../utils/due-day";
import { renderBusinessName } from "../../utils/render-business-name";
import { useFeePaymentPolling } from "../fee-pay/use-fee-payment-polling";
import { Tag } from "../tags";
import { FileUpload } from "../upload/upload-file";

function isPaidInvoice(invoice: {
  paymentStatus?: string;
  invoiceStatus?: string;
  amountToPay?: number | string | null;
} | null | undefined): boolean {
  // Backend payment statuses are PENDING / DIVIDED (part paid) / CLOSED (fully paid).
  // DIVIDED is not "paid": an invoice already part-paid would end the poll at once;
  // the caller detects a partial payment by the drop in amountToPay instead.
  if (invoice?.paymentStatus === "CLOSED" || invoice?.invoiceStatus === "CLOSED") {
    return true;
  }
  // invoice-details treats remaining <= 0 as paid-off even if status lags.
  if (invoice?.amountToPay != null && Number(invoice.amountToPay) <= 0) {
    return true;
  }
  return false;
}

const schema = yup.object({
  amount: yup
    .number()
    .min(0, () => i18n.t("invoice:validation.amountMin"))
    .max(1000000000, () => i18n.t("invoice:validation.amountMax"))
    .typeError(() => i18n.t("common:mustFill"))
    .required(() => i18n.t("common:mustFill"))
    .nullable(),
  method: yup.string().required(() => i18n.t("common:mustFill")).nullable(),
  addInfo: yup.string().max(255, () => i18n.t("common:tooLong")).required(() => i18n.t("common:mustFill")).nullable(),
  creditAccountId: yup
    .string()
    .when("method", {
      is: (val: any) => {
        return val !== "QPAY";
      },
      then: (schema) => schema.required(() => i18n.t("common:mustFill")).nullable(),
    })
    .optional(),
});

type IForm = {
  id: string;
  refCode: string;

  invoiceId: string;
  invoiceRefCode: string;
  receiverBusinessId: string;
  method: string;
  amount: string;
  description: string;

  creditAccountId: string;
  creditAccountBank: string;
  creditAccountName: string;
  creditAccountNumber: string;
  creditAccountCurrency: string;

  debitAccountId: string;
  debitAccountBank: string;
  debitAccountName: string;
  debitAccountNumber: string;
  debitAccountCurrency: string;

  addInfo: string;
  files: string[];

  senderBusinessId: string;
};

/** Хураангуй хавтасны нэг мөр: дээр нь жижиг шошго, доор нь утга. */
function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box>
      <Text size="sm" c="gray.7" fw={500} lh={1.6}>
        {label}
      </Text>
      {children}
    </Box>
  );
}

export function BuyerInvoicePayForm({ action, setAction, onSuccess }: { action: any; setAction: any; setModalOpen?: any; onSuccess?: any }) {
  const { t } = useTranslation("invoice");
  const { t: tc } = useTranslation("common");
  const invoiceStatus = useSelector((state: { general: IGeneral }) => state.general.invoiceStatus);
  const invoicePaymentStatus = useSelector((state: { general: IGeneral }) => state.general.invoicePaymentStatus);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedAccount, setSelectedAccount] = useState<IAccounts | undefined>();
  const [loading, setloading] = useState<boolean>(false);
  const { bankAccounts } = useSelector((state: { general: IGeneral }) => state.general);
  const [qpay, setQpay] = useState<any>({ success: false });
  const poll = useFeePaymentPolling();

  const initialValues: IForm | any = {
    amount: undefined,
    method: undefined,
    creditAccountId: undefined,
    addInfo: undefined,
    files: undefined,
    ...(action[1] && action[1]),
    debitAccountBank: action[1] && action[1].senderAcc?.bankName,
    debitAccountCurrency: action[1] && action[1].senderAcc?.currency,
    debitAccountId: action[1] && action[1].senderAcc?.id,
    debitAccountName: action[1] && action[1].senderAcc?.name,
    debitAccountNumber: action[1] && action[1].senderAcc?.number,
  };

  const form = useForm<IForm | any>({
    mode: "controlled",
    initialValues,
    validate: yupResolver(schema),
  });

  const onSubmit = async (values: IForm) => {
    setloading(true);
    try {
      const res = await InvoiceApi.pay({
        invoiceId: values.id,
        invoiceRefCode: values.refCode,
        receiverBusinessId: values.senderBusinessId,
        method: values.method,
        amount: values.amount,
        description: values.refCode,

        creditAccountId: values.creditAccountId,
        creditAccountBank: values.creditAccountBank,
        creditAccountName: values.creditAccountName,
        creditAccountNumber: values.creditAccountNumber,
        creditAccountCurrency: values.creditAccountCurrency,

        debitAccountId: values.debitAccountId,
        debitAccountBank: values.debitAccountBank,
        debitAccountName: values.debitAccountName,
        debitAccountNumber: values.debitAccountNumber,
        debitAccountCurrency: values.debitAccountCurrency,

        addInfo: values.addInfo,
        files: values.files ? [values.files] : [],
      });

      if (values.method === "QPAY") {
        setQpay({ ...res, success: true });
      } else if (res?.url) {
        // OAuth often lands on cp.dehub.mn/login then Golomt. Poll until the
        // invoice flips; BANK_AUTH_DONE / DAN_AUTH_DONE (see useFeePaymentPolling)
        // close the popup early like de-payment-admin openBankConsent.
        const popup = window.open(
          res.url,
          "golomt-invoice-payment",
          "toolbar=yes,scrollbars=yes,resizable=yes,width=800,height=600,left=200,top=100",
        );
        if (!popup) {
          message.error(t("payForm.popupBlocked"));
          return;
        }

        const invoiceId = values.id;
        const baselineAmount = Number(form.values?.amountToPay);
        poll.start({
          popup,
          checkPaid: async () => {
            const inv = await InvoiceApi.get(invoiceId);
            if (isPaidInvoice(inv)) return true;
            // Partial apply: amount dropped but status still PENDING.
            if (
              Number.isFinite(baselineAmount) &&
              inv?.amountToPay != null &&
              Number(inv.amountToPay) < baselineAmount
            ) {
              return true;
            }
            return false;
          },
          onPaid: () => {
            message.success(t("payForm.paidSuccess"));
            setAction([]);
            onSuccess?.();
          },
          onPending: () => {
            message.success(t("payForm.pendingConfirm"));
            setAction([]);
            onSuccess?.();
          },
        });
      }
    } catch (error: any) {
      message.error(error.message);
    } finally {
      setloading(false);
    }
  };

  // Дүнгүүдийг нэг газраас гаргаж, дутуу талбар дээр "undefined, undefined"
  // гаргахгүйн тулд утгуудыг шүүж нийлүүлнэ.
  const amountToPay = Number(form.values?.amountToPay || 0);
  const enteredAmount = Number(form.values?.amount || 0);
  const remainingBalance = amountToPay - enteredAmount;

  const summaryReceiver = [form.values?.senderBusiness?.regNumber, renderBusinessName(form.values?.senderBusiness?.partner)]
    .filter(Boolean)
    .join(", ");
  const receiveAccount = [form.values?.senderAcc?.number, form.values?.senderAcc?.bankName].filter(Boolean).join(", ");
  const receiveAccountOwner = [form.values?.senderBusiness?.regNumber, form.values?.senderAcc?.name].filter(Boolean).join(", ");

  const invoiceStatusRef = invoiceStatus.find((item: IReference) => item.code === form.values.invoiceStatus);
  const paymentStatusRef = invoicePaymentStatus.find((item: IReference) => item.code === form.values.paymentStatus);

  const isQpay = form.values.method === "QPAY";

  return (
    <>
    {poll.polling && (
      <Portal>
        <Overlay fixed zIndex={3000} backgroundOpacity={0.6} blur={2}>
          <Stack align="center" justify="center" h="100%" gap="md">
            <Loader size="lg" color="white" />
            <Text c="white" fw={600}>{t("payForm.confirming")}</Text>
            <Text c="gray.7" size="sm" ta="center" maw={360}>
              {t("payForm.confirmingHint")}
            </Text>
          </Stack>
        </Overlay>
      </Portal>
    )}
    <Box component="form" onSubmit={form.onSubmit(onSubmit)} px="lg" pb="lg">
      <Grid gap="xl" align="flex-start">
        {/* ── Хураангуй: гар утсан дээр эхэнд, дэлгэц дээр баруун талд ── */}
        <Grid.Col span={{ base: 12, md: 5 }} order={{ base: 1, md: 2 }}>
          <Card withBorder radius="md" padding="lg">
            <Stack gap="md">
              <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
                <Text fw={600} ff="monospace">{form.values?.refCode || "-"}</Text>
                {Tag(invoiceStatusRef?.color || "gray", invoiceStatusRef?.name || "-")}
              </Group>

              <Divider />

              <SummaryRow label={t("payForm.supplierBusiness")}>
                <Text size="sm" fw={500}>
                  {renderBusinessName(form.values?.senderBusiness) || "-"}
                </Text>
              </SummaryRow>

              <SummaryRow label={t("payForm.paymentReceiver")}>
                <Text size="sm" fw={500}>
                  {summaryReceiver || "-"}
                </Text>
              </SummaryRow>

              <SummaryRow label={t("payForm.invoiceDueDate")}>
                <Text size="sm" fw={500}>
                  {dueDay(form.values?.paymentDate) ?? "-"}
                </Text>
              </SummaryRow>

              <Divider />

              <SummaryRow label={t("payForm.invoiceBalance")}>
                <Text fz="h3" fw={700} lh={1.2} style={{ whiteSpace: "nowrap" }}>
                  {currencyFormat(amountToPay)}
                </Text>
              </SummaryRow>

              <SummaryRow label={t("payForm.paymentStatusAmount")}>
                <Group gap="xs" align="center" wrap="wrap">
                  <Text size="sm" fw={500}>
                    {form.values.confirmedAmount ? currencyFormat(form.values.confirmedAmount) : "-"}
                  </Text>
                  {paymentStatusRef ? Tag(paymentStatusRef.color || "gray", t(`paymentStatus.${paymentStatusRef.code}`, { defaultValue: paymentStatusRef.name || "" })) : null}
                </Group>
              </SummaryRow>
            </Stack>
          </Card>
        </Grid.Col>

        {/* ── Төлбөрийн форм ── */}
        <Grid.Col span={{ base: 12, md: 7 }} order={{ base: 2, md: 1 }}>
          <Stack gap="lg">
            <Box>
              <Title order={4}>{t("payForm.paymentInfoTitle")}</Title>
              <Text size="sm" c="dimmed">
                {t("payForm.paymentInfoSubtitle")}
              </Text>
            </Box>

            {qpay.success ? (
              <Stack align="center" gap="md" py="md">
                <Image
                  src={`data:image/jpeg;base64,${qpay?.qr_image || ""}`}
                  alt="QPay QR"
                  w={240}
                  h={240}
                  fit="contain"
                />
                <Text size="sm" c="dimmed" ta="center" maw={320}>
                  {t("payForm.qrInstructions")}
                </Text>
              </Stack>
            ) : (
              <Stack gap="md">
                <Select
                  label={t("payForm.paymentMethod")}
                  placeholder={t("payForm.selectPlaceholder")}
                  searchable
                  clearable
                  required
                  data={[
                    { value: "B2B", label: t("payForm.b2bMethod") },
                    { value: "QPAY", label: "QPay" },
                  ]}
                  {...form.getInputProps("method")}
                  onChange={(value) => {
                    form.setFieldValue("method", value);
                    if (value === "QPAY") form.setFieldValue("amount", form.values?.amountToPay || 0);
                    else form.setFieldValue("amount", undefined);
                  }}
                />

                {!isQpay ? (
                  <Select
                    label={t("payForm.transferAccount")}
                    placeholder={t("payForm.selectPlaceholder")}
                    searchable
                    clearable
                    required
                    data={bankAccounts?.map((item: IAccounts) => ({
                      value: item.id,
                      label: `${item.shortName} / ${item.number}`,
                      disabled: !item.isEnabled,
                    }))}
                    {...form.getInputProps("creditAccountId")}
                    onChange={(value) => {
                      form.setFieldValue("creditAccountId", value);
                      const selected = bankAccounts?.find((item: IAccounts) => item.id === value);
                      setSelectedAccount(selected);
                      form.setFieldValue("creditAccountBank", selected?.bankName);
                      form.setFieldValue("creditAccountName", selected?.name);
                      form.setFieldValue("creditAccountNumber", selected?.number);
                      form.setFieldValue("creditAccountCurrency", selected?.currency);
                    }}
                  />
                ) : null}

                <TextInput
                  label={t("payForm.receiveAccount")}
                  description={receiveAccountOwner || undefined}
                  value={receiveAccount}
                  placeholder={t("payForm.autoFill")}
                  leftSection={<LockSimpleIcon size={16} />}
                  readOnly
                />

                <TextInput
                  label={t("payForm.txDescription")}
                  value={form.values?.refCode || ""}
                  placeholder={t("payForm.autoFill")}
                  leftSection={<LockSimpleIcon size={16} />}
                  readOnly
                />

                <NumberInput
                  label={t("payForm.enterAmount")}
                  description={`${t("payForm.remainingBalance")} ${currencyFormat(remainingBalance)}`}
                  placeholder="0.00"
                  required
                  min={0}
                  hideControls
                  thousandSeparator=","
                  decimalScale={2}
                  leftSection={<CurrencyCircleDollarIcon size={18} />}
                  rightSection={
                    <Button
                      type="button"
                      variant="subtle"
                      size="compact-xs"
                      disabled={isQpay || amountToPay <= 0}
                      onClick={() => form.setFieldValue("amount", amountToPay)}
                    >
                      {t("payForm.payFull")}
                    </Button>
                  }
                  rightSectionWidth={rem(80)}
                  rightSectionPointerEvents="auto"
                  disabled={isQpay}
                  {...form.getInputProps("amount")}
                />

                <Textarea
                  label={t("payForm.additionalNotes")}
                  placeholder={t("payForm.additionalNotesPlaceholder")}
                  required
                  autosize
                  minRows={2}
                  {...form.getInputProps("addInfo")}
                />

                <FileUpload
                  label={t("payForm.attachFile")}
                  nullable
                  value={form.values?.files}
                  error={form.errors?.files && !form.values?.files ? String(form.errors.files) : undefined}
                  onChange={(e) => form.setFieldValue("files", e?.url)}
                  name="files"
                  placeholder={form.values?.files ? `${tc("files")}: ${form.values?.files?.slice(66, 96)}` : t("payForm.attachmentPlaceholder")}
                />
              </Stack>
            )}
          </Stack>
        </Grid.Col>

        {/* ── Үйлдлийн мөр ── */}
        <Grid.Col span={12} order={3}>
          <Divider mb="md" />
          <Group justify="flex-end" gap="sm">
            {qpay.success ? (
              <Button type="button" onClick={() => setAction([])}>
                {t("payForm.done")}
              </Button>
            ) : (
              <>
                <Button type="button" variant="default" onClick={() => setAction([])}>
                  {tc("cancel")}
                </Button>
                <Button loading={loading} leftSection={<CreditCardIcon size={18} />} type="submit">
                  {isQpay ? t("payForm.payQpay") : t("payForm.payB2b")}
                </Button>
              </>
            )}
          </Group>
        </Grid.Col>
      </Grid>
    </Box>
    </>
  );
}